import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, User, Phone, Calendar, Car, GripVertical, MoreVertical, Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader } from '../../components/ui';
import { formatPhone, formatDate } from '../../utils/format';
import { buttonClass } from '../../design-system';

interface LeadCard {
  id: number;
  nome: string;
  telefone: string | null;
  celular: string | null;
  email: string | null;
  origemNome: string | null;
  origemCor: string | null;
  veiculoInteresse: string | null;
  vendedorNome: string | null;
  valorEstimado: number | null;
  prioridade: string;
  dataCadastro: string;
  ultimoContato: string | null;
}

interface Coluna {
  id: number;
  nome: string;
  cor: string;
  ordem: number;
  leads: LeadCard[];
}

const prioridadeColors: Record<string, string> = {
  ALTA: 'bg-red-500',
  MEDIA: 'bg-yellow-500',
  BAIXA: 'bg-green-500',
};

export function LeadsKanban() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const [colunas, setColunas] = useState<Coluna[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draggingLead, setDraggingLead] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<number | null>(null);

  const fetchKanban = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const response = await api.get<Coluna[]>('/leads/kanban', { signal });
      setColunas(response.data);
    } catch (error: unknown) {
      const err = error as { name?: string; code?: string };
      if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
        showError('Erro ao carregar leads');
      }
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    const controller = new AbortController();
    fetchKanban(controller.signal);
    return () => controller.abort();
  }, [fetchKanban]);

  const handleDragStart = (e: React.DragEvent, leadId: number) => {
    e.dataTransfer.setData('leadId', leadId.toString());
    setDraggingLead(leadId);
  };

  const handleDragEnd = () => {
    setDraggingLead(null);
    setDropTarget(null);
  };

  const handleDragOver = (e: React.DragEvent, etapaId: number) => {
    e.preventDefault();
    setDropTarget(etapaId);
  };

  const handleDrop = async (e: React.DragEvent, etapaId: number) => {
    e.preventDefault();
    const leadId = parseInt(e.dataTransfer.getData('leadId'));
    setDropTarget(null);
    setDraggingLead(null);

    // Otimistic update
    setColunas((prev) => {
      const newColunas = prev.map((col) => ({
        ...col,
        leads: col.leads.filter((l) => l.id !== leadId),
      }));

      const lead = prev.flatMap((c) => c.leads).find((l) => l.id === leadId);
      if (lead) {
        const targetCol = newColunas.find((c) => c.id === etapaId);
        if (targetCol) {
          targetCol.leads.push(lead);
        }
      }

      return newColunas;
    });

    try {
      await api.put(`/leads/${leadId}/etapa`, { etapaId });
      showSuccess('Lead movido com sucesso');
    } catch {
      showError('Erro ao mover lead');
      fetchKanban(undefined); // Recarrega em caso de erro
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <PageHeader title="Funil de Vendas" subtitle="Gerencie seus leads no kanban">
        <button onClick={() => navigate('/crm/leads/novo')} className={buttonClass('primary')}>
          <Plus className="w-4 h-4" />
          Novo Lead
        </button>
      </PageHeader>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto pb-4">
        <div className="flex gap-4 h-full min-w-max">
          {colunas.map((coluna) => (
            <div
              key={coluna.id}
              className={`flex w-80 flex-shrink-0 flex-col overflow-hidden rounded-2xl border bg-slate-50/70 transition-colors ${
                dropTarget === coluna.id ? 'border-primary-300 bg-primary-50/60 ring-2 ring-primary-200' : 'border-slate-200/70'
              }`}
              onDragOver={(e) => handleDragOver(e, coluna.id)}
              onDrop={(e) => handleDrop(e, coluna.id)}
              onDragLeave={() => setDropTarget(null)}
            >
              {/* Accent superior por etapa */}
              <div className="h-1 w-full" style={{ backgroundColor: coluna.cor }} />

              <div className="flex flex-1 flex-col p-4">
                {/* Column Header */}
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: coluna.cor }} />
                    <h3 className="font-bold text-slate-900">{coluna.nome}</h3>
                    <span className="rounded-full bg-slate-200/80 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-600">
                      {coluna.leads.length}
                    </span>
                  </div>
                  <button className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-200/70 hover:text-slate-600">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>

                {/* Cards */}
                <div className="flex-1 space-y-3 overflow-y-auto">
                  {coluna.leads.map((lead) => (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead.id)}
                      onDragEnd={handleDragEnd}
                      onClick={() => navigate(`/crm/leads/${lead.id}`)}
                      className={`cursor-pointer rounded-xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)] ${
                        draggingLead === lead.id ? 'rotate-2 scale-105 opacity-50' : ''
                      }`}
                    >
                      {/* Priority indicator */}
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 cursor-grab text-slate-300" />
                          <div
                            className={`h-2 w-2 rounded-full ${prioridadeColors[lead.prioridade] || 'bg-slate-400'}`}
                            title={`Prioridade ${lead.prioridade}`}
                          />
                        </div>
                        {lead.origemNome && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: lead.origemCor || '#94a3b8' }} />
                            {lead.origemNome}
                          </span>
                        )}
                      </div>

                      {/* Lead info */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <span className="truncate font-semibold text-slate-900">
                            {lead.nome}
                          </span>
                        </div>

                        {(lead.celular || lead.telefone) && (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Phone className="h-4 w-4 text-slate-400" />
                            <span>{formatPhone(lead.celular || lead.telefone)}</span>
                          </div>
                        )}

                        {lead.veiculoInteresse && (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Car className="h-4 w-4 text-slate-400" />
                            <span className="truncate">{lead.veiculoInteresse}</span>
                          </div>
                        )}

                        <div className="flex items-center justify-between border-t border-slate-100 pt-2">
                          <div className="flex items-center gap-1 text-xs text-slate-400">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(lead.dataCadastro)}</span>
                          </div>
                          {lead.vendedorNome && (
                            <span className="text-xs text-slate-400">
                              {lead.vendedorNome.split(' ')[0]}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {coluna.leads.length === 0 && (
                    <div className="py-8 text-center text-sm text-slate-400">
                      Arraste leads para cá
                    </div>
                  )}
                </div>

                {/* Add lead button */}
                <button
                  onClick={() => navigate(`/crm/leads/novo?etapa=${coluna.id}`)}
                  className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg py-2 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                >
                  <Plus className="h-4 w-4" />
                  Adicionar lead
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
